/**
 * The search box on the Settings roster tabs (Employees, Equipment): which
 * rows a query keeps, and the two lines that describe the result.
 *
 * Same voice as the Compare rates window's search on purpose — the two boxes
 * sit one click apart and answer the same kind of question, so the counts line
 * is built by that window's own `countsLine` rather than a second copy of it
 * that could drift.
 *
 * Client-side over the rows already loaded: a roster is at most a few hundred
 * entries, and the heading's count stays the roster's total regardless.
 */
import type { SettingsEmployee, SettingsEquipment } from '@coffman-tm/shared';
import { countsLine } from './rateComparison';

export type RosterSearchKind = 'employees' | 'equipment';

/** The query as it is matched — trimmed and case-folded. Blank means no search. */
function normalize(query: string): string {
  return query.trim().toLowerCase();
}

/** True when the box holds something to search by. Whitespace alone is not a search. */
export function isSearching(query: string): boolean {
  return normalize(query) !== '';
}

function matchesAny(fields: ReadonlyArray<string | null | undefined>, q: string): boolean {
  return fields.some((field) => (field ?? '').toLowerCase().includes(q));
}

/**
 * Employees matching the query by name, HeavyJob ID or classification —
 * case-insensitive substring. The ID is what the foreman's PDF prints, so it
 * is as likely a search term as the name; classification finds "every
 * operator" in one go.
 */
export function filterEmployees<
  T extends Pick<SettingsEmployee, 'name' | 'heavyjob_employee_id' | 'classification'>,
>(rows: ReadonlyArray<T>, query: string): T[] {
  const q = normalize(query);
  if (q === '') return [...rows];
  return rows.filter((row) =>
    matchesAny([row.name, row.heavyjob_employee_id, row.classification], q),
  );
}

/**
 * Equipment matching the query by asset ID or description — case-insensitive
 * substring. Type and rate are deliberately not searched: "rental" would match
 * half the list and a number typed here is far likelier part of an asset ID.
 */
export function filterEquipment<T extends Pick<SettingsEquipment, 'asset_id' | 'description'>>(
  rows: ReadonlyArray<T>,
  query: string,
): T[] {
  const q = normalize(query);
  if (q === '') return [...rows];
  return rows.filter((row) => matchesAny([row.asset_id, row.description], q));
}

/**
 * The rows the table renders: every row the search matched, plus any row that
 * is open for editing whether it matches or not — in roster order.
 *
 * A row's edit form lives in the row, so filtering an open row out would
 * unmount it and throw away whatever was typed into it. Typing a search is
 * not a request to discard an edit. Once the edit closes, the row goes back
 * to answering to the search like every other.
 */
export function withOpenEdits<T extends { id: string }>(
  rows: ReadonlyArray<T>,
  matched: ReadonlyArray<T>,
  openIds: ReadonlySet<string>,
): T[] {
  const matchedIds = new Set(matched.map((row) => row.id));
  return rows.filter((row) => matchedIds.has(row.id) || openIds.has(row.id));
}

/**
 * The muted line over a filtered table — "12 of 140 match "cat"" — or null
 * when nothing is typed. Unfiltered it says nothing: the section heading
 * already carries the roster's count (RULES.md 1.5), and a second copy of the
 * same number under it would state the fact twice.
 */
export function rosterCountsLine({
  kind,
  total,
  shown,
  query,
}: {
  kind: RosterSearchKind;
  total: number;
  shown: number;
  query: string;
}): string | null {
  if (!isSearching(query)) return null;
  return countsLine({ kind, total, shown, query, onlyDifferences: false });
}

/**
 * The empty state when a search matched nothing, with the query quoted back —
 * so an empty table reads as "your search" and never as "no roster".
 * "equipment" is a mass noun, hence "matches".
 */
export function noMatchText(kind: RosterSearchKind, query: string): string {
  const q = query.trim();
  return kind === 'employees' ? `No employees match "${q}".` : `No equipment matches "${q}".`;
}
